import React, { useState, useEffect, useCallback } from "react";
import { Progress, List, Badge, Tooltip, Spin } from "antd";
import { getCapacityUtilization, getAllMachines } from "../../../api/dashboardApi";

const MachineUtilization = () => {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMachines = useCallback(async () => {
    try {
      // Get all machines for their status
      const machineList = await getAllMachines({ is_active: true });
      const allMachines = machineList || [];

      // Get capacity / utilisation for last 7 days
      const todayStr = new Date().toISOString().slice(0, 10);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const weekAgoStr = weekAgo.toISOString().slice(0, 10);

      let utilMap = {};
      try {
        const cap = await getCapacityUtilization(weekAgoStr, todayStr);
        (cap.utilization || []).forEach((u) => {
          utilMap[u.machine_id] = u;
        });
      } catch (_) {
        /* capacity endpoint may fail – that's fine */
      }

      const merged = allMachines.map((m) => {
        const util = utilMap[m.id] || {};
        return {
          id: m.id,
          name: m.name || m.code,
          code: m.code,
          type: m.type,
          status: m.status || "operational",
          utilization_pct: util.utilization_pct || 0,
          scheduled_minutes: util.scheduled_minutes || 0,
          available_minutes: util.available_minutes || 0,
          schedule_count: util.schedule_count || 0,
        };
      });
      setMachines(merged);
    } catch (error) {
      console.error("Failed to load machine status", error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMachines();
    const interval = setInterval(fetchMachines, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchMachines]);

  const getStatusBadge = (status) => {
    const map = {
      operational: { antStatus: "success", text: "Running" },
      maintenance: { antStatus: "error", text: "Maintenance" },
      breakdown: { antStatus: "error", text: "Breakdown" },
    };
    const cfg = map[status] || { antStatus: "default", text: status };
    return <Badge status={cfg.antStatus} text={cfg.text} />;
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Spin />
      </div>
    );
  }

  if (machines.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: 20, color: "#999" }}>
        No machines configured
      </div>
    );
  }

  return (
    <List
      size="small"
      dataSource={machines}
      renderItem={(machine) => (
        <List.Item>
          <List.Item.Meta
            title={machine.name}
            description={getStatusBadge(machine.status)}
          />
          <Tooltip
            title={`${machine.utilization_pct}% utilisation (${machine.schedule_count} jobs scheduled)`}
          >
            <Progress
              type="circle"
              percent={machine.utilization_pct}
              width={50}
              strokeColor={
                machine.status === "maintenance" || machine.status === "breakdown"
                  ? "#f5222d"
                  : machine.utilization_pct > 80
                  ? "#52c41a"
                  : machine.utilization_pct > 50
                  ? "#1890ff"
                  : "#faad14"
              }
            />
          </Tooltip>
        </List.Item>
      )}
    />
  );
};

export default MachineUtilization;
